import { Action, Status } from './enums';
import dayjs from 'dayjs';
import { capitalize } from '@mui/material/utils';

/**
 * Base URL for API (no trailing slash). Used so image requests hit the same server in production.
 * In browser, falls back to current origin so images work when frontend and API are same origin (e.g. nginx proxy).
 */
export const getApiBaseUrl = () => {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost:8086';
};

/**
 * Builds full URL for a photo stored as relative path (e.g. /api/v1/users/xxx/photo).
 * Use for <img src> and Avatar; backend stores BLOB and serves at /api/v1/users/{id}/photo.
 * On Linux/production: set REACT_APP_API_URL to your API base, or serve frontend and API from same origin.
 * @param {string|null|undefined} photoUrl - Relative path from API (e.g. /api/v1/users/abc123/photo)
 * @returns {string|null} Full URL or null if no photoUrl
 */
export const getImageUrl = (photoUrl) => {
  if (!photoUrl || typeof photoUrl !== 'string') return null;
  const base = getApiBaseUrl();
  return photoUrl.startsWith('http') ? photoUrl : `${base}${photoUrl}`;
};

/**
 * Formats a date string to a standard format
 * @param {string} date - The date string to format
 * @returns {string|null} The formatted date string or null if the input is invalid
 */
export const formatDate = (date) => {
  if (!date) return null;
  return dayjs(date).format('YYYY-MM-DDTHH:mm:ss'); 
};



export const permit = (condition, allowed) => {
    return allowed.includes(condition);
};

export const getDetailsRoute = (route, id) => {
    return route.replace(":id", id.toString());
};

export const getRoute = (route, id) => {
    return route.replace(":id", id.toString());
};

export const getStatusColor = (status) => {
    const s = status?.toUpperCase();
    if(s === Status.ACTIVE || s === Status.COMPLETED)
        return "success";
    if(s === Status.PENDING || s === Status.DISABLED)
        return "warning";
    if(s === Status.DELETED || s === Status.REJECTED){
        return "error";
    }

    return "primary";
};

export const getStatusForAction = (action) => {
    switch (action) {
        case Action.Activate:
            return Status.ACTIVE;
        case Action.Delete:
            return Status.DELETED;
        case Action.Disable:
            return Status.DISABLED;
        case Action.Approve:
            return Status.APPROVED;
        case Action.Authorize:
            return Status.AUTHORIZED;
        case Action.Reject:
            return Status.REJECTED;
        case Action.Un_Authorize:
            return Status.UN_AUTHORIZED;
        case Action.Reset:
        case Action.Update:
        case Action.Read:
            return Status.DEFAULT;
        default:
            return Status.DEFAULT;
    }
};


export const toSentenceCase = (text) => {
    if(text == null || text == undefined){
        return "";
    }

    return text.split(" ").map(a => capitalize(a)).join(" ");
};

/** FORM 1 / FORM 5 students are starting; first-term amount is Seminarian Needs, not previous debt. */
export const isSeminarianNeedsClass = (classTitle) => {
    const normalized = String(classTitle || "")
        .toUpperCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    return (
        normalized === "FORM 1" ||
        normalized === "FORM1" ||
        normalized === "FORM I" ||
        normalized === "FORM 5" ||
        normalized === "FORM5" ||
        normalized === "FORM V"
    );
};

export const getFirstTermDebtLabel = (classTitle, { short = false } = {}) => {
    if (isSeminarianNeedsClass(classTitle)) return "Seminarian Needs (Vifaa na Sare)";
    return short ? "T-1-Debt" : "First Term Debt";
};
